import type * as React from "react";

import { cn } from "@/lib/utils";

const tones = {
  bg: "bg-bg",
  surface: "bg-surface border-t border-ink/10",
  ink: "bg-ink text-on-dark",
  none: "",
} as const;

const paddings = {
  none: "",
  tight: "py-8 sm:py-10",
  default: "py-14 sm:py-20 lg:py-28",
  loose: "py-16 sm:py-24 lg:py-[8.25rem]",
} as const;

type SectionProps = React.HTMLAttributes<HTMLElement> & {
  tone?: keyof typeof tones;
  padding?: keyof typeof paddings;
  /** Full-bleed sections keep their own horizontal padding (or none at all). */
  bleed?: boolean;
  as?: "section" | "div" | "article" | "aside" | "footer";
};

/**
 * The single rhythm primitive. Long pages need changes of pace, so tone and
 * padding are explicit per section rather than a rigid alternating sequence
 * that breaks when the family hides one section (brief §5, requirement 6).
 */
export function Section({
  tone = "none",
  padding = "default",
  bleed = false,
  as: Tag = "section",
  className,
  children,
  ...props
}: SectionProps) {
  return (
    <Tag className={cn(tones[tone], paddings[padding], className)} {...props}>
      {bleed ? children : <div className="px-5 sm:px-8 lg:px-16">{children}</div>}
    </Tag>
  );
}

export function Container({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mx-auto w-full max-w-page", className)} {...props} />;
}
